import { useEffect, useState } from "react";
import { MdDelete } from "react-icons/md";
import api from "../../configs/api";
import { confirmAction } from "../../utils/confirmAction";
import { formatDate } from "../../utils/helpers";
import { showError, showSuccess } from "../../utils/toastUtils";

const SuperAdminInteriorList = () => {
  const [leads, setLeads] = useState([]);

  const statusLabels = {
    no_response: "No Response",
    in_progress: "In Progress",
    site_visit_done: "Site Visit Done",
    bill_ok: "Bill Ok",
    reject: "Rejected",
    completed: "Completed",
  };

  const statusColors = {
    no_response: "text-orange-500",
    in_progress: "text-blue-500",
    site_visit_done: "text-yellow-500",
    bill_ok: "text-green-500",
    reject: "text-red-500",
    completed: "text-green-700",
  };

  const statusOptions = Object.keys(statusLabels);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const res = await api.get("/interior-leads");
      setLeads(res.data.list);
    } catch (err) {
      console.error(err);
      showError("Failed to load leads");
    }
  };

  const handleStatusChange = async (leadId, oldStatus, newStatus) => {
    if (oldStatus === newStatus) return;

    const confirmed = await confirmAction(
      "Update Lead Status?",
      `Are you sure you want to mark this lead as "${statusLabels[newStatus]}"?`,
      "Yes, Update"
    );

    if (!confirmed.isConfirmed) return;

    try {
      await api.put(`/interior-leads/status/${leadId}`, {
        status: newStatus,
      });

      showSuccess("Status updated!");

      setLeads((prev) =>
        prev.map((l) => (l._id === leadId ? { ...l, status: newStatus } : l))
      );
    } catch (err) {
      console.error(err);
      showError("Failed to update status");
    }
  };

  const handleDeleteLead = async (leadId) => {
    const confirmed = await confirmAction(
      "Delete Lead?",
      "This action cannot be undone.",
      "Delete"
    );

    if (!confirmed.isConfirmed) return;

    try {
      await api.delete(`/interior-leads/${leadId}`);
      showSuccess("Lead deleted!");

      setLeads((prev) => prev.filter((l) => l._id !== leadId));
    } catch (err) {
      console.error(err);
      showError("Failed to delete lead");
    }
  };

  return (
    <div className="bg-gray-50 border rounded-lg shadow-sm">
      {/* HEADER */}
      <h1 className="text-[#212B36] rounded-t-lg border-b-2 bg-[#F9F9F9] px-4 py-6 font-semibold">
        Interior Leads
      </h1>

      {/* TABLE */}
      <div className="bg-white text-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#E9EDF4]">
              <tr>
                <th className="px-4 py-3 text-left">No</th>
                <th className="px-4 py-3 text-left w-20">Lead Name</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Property Type</th>
                <th className="px-4 py-3 text-left"> Date</th>
                <th className="px-4 py-3 text-left">Phone</th>
                <th className="px-4 py-3 text-left">Property Name</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Action</th>
              </tr>
            </thead>

            <tbody className="divide-y ">
              {leads.map((lead, index) => (
                <tr key={lead._id} className="hover:bg-gray-50 ">
                  <td className="px-4 py-3">{index + 1}</td>

                  <td className="px-4 text-nowrap">{lead.fullName}</td>

                  <td className="px-4">{lead.email}</td>

                  <td className="px-4">{lead.propertyType}</td>

                  <td className="px-4">{formatDate(lead.createdAt)}</td>

                  <td className="px-4">{lead.phone}</td>

                  <td className="px-4">{lead.propertyName}</td>

                  {/* STATUS DROPDOWN */}
                  <td className="px-4">
                    <select
                      value={lead.status}
                      onChange={(e) =>
                        handleStatusChange(
                          lead._id,
                          lead.status,
                          e.target.value
                        )
                      }
                      className={`border px-2 py-1 rounded ${
                        statusColors[lead.status]
                      }`}
                    >
                      {statusOptions.map((status) => (
                        <option key={status} value={status}>
                          {statusLabels[status]}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* ACTION MENU */}
                  <td className="px-2">
                    <button
                      onClick={() => handleDeleteLead(lead._id)}
                      className="text-red-500 hover:text-red-700 font-medium"
                    >
                      <MdDelete className="size-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminInteriorList;
